import {
  ColumnsConfig,
  getAllKeys,
  getColumnDisplayName,
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_COLUMN_WIDTH,
} from "src/cases/caseView/blocks/cellTypes";
import {
  DiffTableHeaderCell,
  DiffTableValueCell,
} from "src/cases/caseView/history/events/diffTableCells";
import { PropertyCell } from "src/cases/caseView/history/events/SingleRecordDiffTable";
import { TableRecordShape } from "src/cases/caseView/history/events/tableBlockDiff";
import { pluralize } from "src/utils/stringUtils";

type DiffSide = "before" | "after";

// Same fixed column sizing as the row diff so both snapshots line up.
const COLUMN_STYLE = {
  width: TABLE_COLUMN_WIDTH,
  minWidth: TABLE_COLUMN_MIN_WIDTH,
  maxWidth: TABLE_COLUMN_WIDTH,
} as const;

const SIDE_TINT: Record<DiffSide, string> = {
  before: "bg-red-50",
  after: "bg-green-50",
};

const describeShape = (shape: TableRecordShape): string =>
  shape.kind === "multi"
    ? `Table with ${pluralize(shape.record.length, "row")}`
    : "Single record";

/**
 * An edit that turned a table into a single record (or the reverse) has no
 * cells to pair up, so each side is shown whole: the before shape as removed
 * (red) above the after shape as added (green).
 */
export const ReshapedRecordDiff: React.FC<{
  before: TableRecordShape;
  after: TableRecordShape;
  columnsConfig: ColumnsConfig;
}> = ({ before, after, columnsConfig }) => (
  <div className="flex flex-col gap-y-4" data-loc="table-changes-reshaped">
    <SnapshotSection
      columnsConfig={columnsConfig}
      shape={before}
      side="before"
    />
    <SnapshotSection columnsConfig={columnsConfig} shape={after} side="after" />
  </div>
);

const SnapshotSection: React.FC<{
  shape: TableRecordShape;
  side: DiffSide;
  columnsConfig: ColumnsConfig;
}> = ({ shape, side, columnsConfig }) => (
  <div
    className="flex flex-col gap-y-1.5"
    data-loc={`table-changes-reshaped-${side}`}
  >
    <div className="flex items-center gap-x-1 font-inter-medium-12px">
      <span className={side === "before" ? "text-gray-500" : "text-gray-800"}>
        {side === "before" ? "Before" : "After"}
      </span>
      <span className="font-inter-normal-12px text-gray-500">
        · {describeShape(shape)}
      </span>
    </div>
    {shape.kind === "multi" ? (
      <MultiRecordSnapshot
        columnsConfig={columnsConfig}
        records={shape.record}
        side={side}
      />
    ) : (
      <SingleRecordSnapshot
        columnsConfig={columnsConfig}
        record={shape.record}
        side={side}
      />
    )}
  </div>
);

const MultiRecordSnapshot: React.FC<{
  records: Extract<TableRecordShape, { kind: "multi" }>["record"];
  side: DiffSide;
  columnsConfig: ColumnsConfig;
}> = ({ records, side, columnsConfig }) => {
  const keys = getAllKeys(records);

  return (
    <div className="decideScrollbar overflow-x-auto">
      <table className="border-collapse">
        <thead>
          <tr>
            {keys.map((key) => (
              <DiffTableHeaderCell
                key={key}
                dataLoc={`table-changes-snapshot-header-${side}-${key}`}
                muted={side === "before"}
                style={COLUMN_STYLE}
              >
                {getColumnDisplayName(key, columnsConfig)}
              </DiffTableHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {keys.map((key) => (
                <DiffTableValueCell
                  key={key}
                  columnKey={key}
                  columnsConfig={columnsConfig}
                  dataLoc={`table-changes-snapshot-${side}-${rowIndex}-${key}`}
                  hasValue={key in row}
                  muted={side === "before"}
                  style={COLUMN_STYLE}
                  tint={SIDE_TINT[side]}
                  value={row[key]}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SingleRecordSnapshot: React.FC<{
  record: Extract<TableRecordShape, { kind: "single" }>["record"];
  side: DiffSide;
  columnsConfig: ColumnsConfig;
}> = ({ record, side, columnsConfig }) => (
  <div className="decideScrollbar overflow-x-auto">
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <DiffTableHeaderCell style={{ width: TABLE_COLUMN_WIDTH }}>
            Property
          </DiffTableHeaderCell>
          <DiffTableHeaderCell muted={side === "before"}>
            Value
          </DiffTableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {Object.keys(record).map((key) => (
          <tr key={key}>
            <PropertyCell columnKey={key} columnsConfig={columnsConfig} />
            <DiffTableValueCell
              columnKey={key}
              columnsConfig={columnsConfig}
              dataLoc={`table-changes-snapshot-${side}-${key}`}
              hasValue
              muted={side === "before"}
              tint={SIDE_TINT[side]}
              value={record[key]}
            />
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
